import { idOf } from './utils.js';

// Workspace responses deliberately omit commerce and timeline data.
export function mergeWorkspace(current, workspace) {
  if (idOf(current) === idOf(workspace) && Number(workspace.version) < Number(current.version)) return current;
  const sameCustomer = idOf(current) === idOf(workspace) && current.customerId === workspace.customerId;
  return { ...current, ...workspace,
    messages: current.messages || [], events: current.events || [], attachments: current.attachments || [],
    orders: sameCustomer ? current.orders || [] : [],
    shopifyNextCursor: sameCustomer ? current.shopifyNextCursor : null,
    shopifyStale: sameCustomer ? current.shopifyStale : true,
    shopifyRefreshing: sameCustomer && current.shopifyRefreshing,
    shopifyRefreshError: sameCustomer ? current.shopifyRefreshError : null,
    customer: { ...workspace.customer, shopify: sameCustomer ? current.customer?.shopify : undefined }
  };
}

// Patch only changed nodes; retain focused controls and native disclosure state.
export function patchLiveNode(current, next, options = {}) {
  if (options.preserveEditors && current.nodeType === 1 && current.id === 'composer-text') return;
  if (options.preserveForms && current.nodeType === 1 && current.matches('form, input, textarea, select, [data-live-preserve]')) return;
  if (current.isEqualNode(next)) return;
  if (current.nodeType !== next.nodeType || current.nodeName !== next.nodeName) { current.replaceWith(next.cloneNode(true)); return; }
  if (current.nodeType !== 1) { current.nodeValue = next.nodeValue; return; }
  if (current === current.ownerDocument.activeElement && current.matches('input, textarea, select, [contenteditable]')) return;
  for (const attr of [...current.attributes]) {
    if (attr.name === 'open' && current.tagName === 'DETAILS') continue;
    if (!next.hasAttribute(attr.name)) current.removeAttribute(attr.name);
  }
  for (const attr of next.attributes) {
    if (attr.name === 'open' && current.tagName === 'DETAILS') continue;
    if (current.getAttribute(attr.name) !== attr.value) current.setAttribute(attr.name, attr.value);
  }
  const key = (node) => node.nodeType === 1 ? node.id || node.getAttribute('data-live-key') || (node.classList.contains('ticket-row') ? node.getAttribute('data-ticket-id') : null) : null;
  const keyed = new Map([...current.childNodes].filter(key).map((node) => [key(node), node]));
  const children = [...next.childNodes];
  children.forEach((child, index) => {
    if (!key(child) && key(current.childNodes[index] || {})) { current.insertBefore(child.cloneNode(true), current.childNodes[index]); return; }
    const match = key(child) && keyed.get(key(child));
    if (match && match !== current.childNodes[index]) current.insertBefore(match, current.childNodes[index] || null);
    else if (key(child) && key(current.childNodes[index] || {}) !== key(child)) { current.insertBefore(child.cloneNode(true), current.childNodes[index] || null); return; }
    if (current.childNodes[index]) patchLiveNode(current.childNodes[index], child, options);
    else current.append(child.cloneNode(true));
  });
  while (current.childNodes.length > children.length) current.lastChild.remove();
}

// Keep the scroll containers themselves mounted. Only use this for background updates.
export function patchLiveRegion(selector, html, { prepare } = {}) {
  const current = document.querySelector(selector);
  if (!current) return;
  const template = document.createElement('template');
  template.innerHTML = html;
  const next = template.content.querySelector(selector);
  if (!next) return;
  prepare?.(next);
  const scroll = [current, ...current.querySelectorAll('*')].filter(node => node.scrollTop || node.scrollLeft).map(node => [node, node.scrollTop, node.scrollLeft]);
  const focused = current.contains(document.activeElement) ? document.activeElement : null;
  const selection = focused && typeof focused.selectionStart === 'number' ? [focused.selectionStart, focused.selectionEnd] : null;
  patchLiveNode(current, next, { preserveForms: true });
  if (focused?.isConnected && document.activeElement !== focused) { focused.focus({ preventScroll: true }); if (selection) focused.setSelectionRange(...selection); }
  for (const [node, top, left] of scroll) if (node.isConnected) { node.scrollTop = top; node.scrollLeft = left; }
}
